import { noop } from 'rxjs/util/noop';

import { EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { FormGroup } from '@angular/forms';

export abstract class CustomElement implements OnInit {

    /**
     * The FormGroup instance.
     */
    @Input() form: FormGroup;

    /**
     * The element type.
     */
    @Input() type: string;

    /**
     * The element name.
     */
    @Input() name: string = null;

    /**
     * Indicates if autofocus is enabled.
     */
    @Input() autofocus: boolean;

    /**
     * Indicates if the element is disabled.
     */
    @Input()
    set disabled(value: boolean) {
        if (value === false) {
            this.enable();
        } else {
            this.disable();
        }

        this.isDisabled = value;
    }

    get disabled() {
        return this.isDisabled;
    }

    /**
     * Indicates if the element is disabled.
     */
    protected isDisabled: boolean;

    /**
     * Indicates if the element is read only.
     */
    @Input() readonly: boolean;

    /**
     * Indicates if the element is required.
     */
    @Input() required: boolean;

    /**
     * The element classes.
     */
    @Input() class: string = null;

    /**
     * Indicates if the element is hidden.
     */
    @Input() hidden: boolean;

    /**
     * The element id.
     */
    @Input() id: string = null;

    /**
     * The element tab index.
     */
    @Input() tabindex: number = null;

    /**
     * The element title.
     */
    @Input() title: string = null;

    @Input() leftSymbol = null;

    @Input() rightSymbol = null;

    /*
     * Angular properties.
     */

    /**
     * The change event emitter.
     *
     * @type {EventEmitter<any>}
     */
    @Output() change: EventEmitter<any> = new EventEmitter();

    /**
     * The initial event emitter.
     *
     * @type {EventEmitter<any>}
     */
    protected initialValue: EventEmitter<any> = new EventEmitter();

    /**
     * Indicates if the element has had an initial value.
     *
     * @type {boolean}
     */
    protected initialized = false;

    /**
     * Angular inner value.
     * Be careful when changing this property, it may break the element.
     *
     * @type {string}
     */
    protected innerValue: any = '';

    /**
     * Angular on touched callback.
     * Be careful when changing this property, it may break the element.
     *
     * @type {() => void}
     */
    protected onTouchedCallback: () => void = noop;

    /**
     * Angular on change callback.
     * Be careful when changing this property, it may break the element.
     *
     * @type {() => void}
     */
    protected onChangeCallback: (_: any) => void = noop;

    /**
     * OnInit.
     */
    public ngOnInit() {
        this.generateId();
        this.executeMutators();
    }

    /**
     * Generate and set a unique id if not set.
     */
    protected generateId() {
        if (!this.id || typeof this.id === 'number') {
            this.id = UUID.UUID();
        } else {
            this.id = this.id.split('.').join('');
        }
    }

    /**
     * Execute the property mutators.
     */
    protected executeMutators() {
        this.name      = this.name ? this.name : null;
        this.autofocus = this.autofocus ? true : null;
        this.disabled  = this.disabled ? true : null;
        this.readonly  = this.readonly ? true : null;
        this.required  = this.required ? true : null;
        this.class     = this.class ? this.class : null;
        this.hidden    = this.hidden ? true : null;
        this.id        = this.id ? this.id : null;
        this.tabindex  = this.tabindex ? this.tabindex : null;
        this.title     = this.title ? this.title : null;
    }

    /**
     * Enable the element.
     */
    protected enable() {
        // @Todo
    }

    /**
     * Disable the element.
     */
    protected disable() {
        // @Todo
    }

    /**
     * Check if element is from given type.
     *
     * @param type
     * @returns {any}
     */
    public isType(type: string | Array<string>) {
        if (type instanceof Array) {
            return type['includes'](this.type);
        }

        return this.type === type;
    }

    /**
     * Check if the value gets it's initial value.
     *
     * @param value
     */
    protected checkInitValue(value) {
        if (!this.initialized) {
            // We'll check if the value is not empty. Zero or false does not mean the value is
            // empty so we'll let it continue.
            if (value || value === 0 || value === false) {
                if (value !== this.innerValue) {
                    this.initialized = true;
                    this.innerValue  = value;
                    this.change.emit(value);
                    this.initialValue.emit(value);
                }

                this.innerValue = value;
            }
        }
    }

    /*
     * Angular methods.
     * Be careful below this point, you may break the element when making changes.
     */

    /**
     * Angular value getter.
     * Be careful when changing this method, it may break the element.
     *
     * @returns {any}
     */
    public get val(): any {
        return this.innerValue;
    };

    /**
     * Angular value setter.
     * Be careful when changing this method, it may break the element.
     *
     * @param v
     */
    public set val(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;

            if (v !== undefined && v !== null && v !== '') {
                this.onChangeCallback(v);
            }

            this.change.emit(this.innerValue);
        }
    }

    /**
     * Angular on blur callback.
     * Be careful when changing this method, it may break the element.
     */
    protected onBlur() {
        this.onTouchedCallback();
    }

    /**
     * Angular write value method which watches value changes.
     * Be careful when changing this method, it may break the element.
     *
     * @param value
     */
    protected writeValue(value: any) {
        // Not an Angular method, but does not change the default behaviour.
        this.checkInitValue(value);

        if (value !== this.innerValue) {
            // inner value is already set by the 'val setter' -> change detection is not working when innerValue is set here
            // this.innerValue = value;
            this.val = value;
        }
    }

    /**
     * Angular register on change.
     * Be careful when changing this method, it may break the element.
     *
     * @param fn
     */
    protected registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    /**
     * Angular register on touched.
     * Be careful when changing this method, it may break the element.
     *
     * @param fn
     */
    protected registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/elements/classes/custom-element.class.ts